import React, { useEffect, useMemo, useState } from 'react';
import { Menu } from './MenuData';
import { useDispatch, useSelector } from 'react-redux';
import './VerticalNavBar.css';
import { Link } from 'react-router-dom';
import { logOut } from '../../../redux/userRedux';
import { styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import UserState from '../../../Data/UserState';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const SmallAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: '#E6A341',
  width: 48,
  height: 48,
  border: `2px solid ${theme.palette.background.paper}`,
}));

function stringAvatar(name: string) {
  const safeName = (name || 'Invitada').trim();
  const parts = safeName.split(' ').filter(Boolean);

  if (parts.length > 1) {
    return {
      children: `${parts[0][0]}${parts[1][0]}`,
    };
  }

  return {
    children: `${parts[0][0]}`,
  };
}

interface NavBadgeMeta {
  count?: string;
  live?: boolean;
}

const navBadges: Record<string, NavBadgeMeta> = {
  Biblioteca: { count: '12' },
  Retos: { count: '3' },
  'En Vivo': { live: true },
  Chat: { count: '7' },
};

const principalItems = ['Inicio', 'Clases de Yoga', 'Biblioteca', 'Retos'];
const communityItems = ['En Vivo', 'Chat'];
const spaceItems = ['Mujer Soberana'];

const VerticalNavBar = () => {
  const [stickyClass, setStickyClass] = useState('');
  const [showVerticalMenu, setShowVerticalMenu] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const userId = useSelector((state: UserState) => state.user.currentUser);
  const userName: string = userId?.fullName || 'Luna Martinez';
  const isAdmin: boolean = Boolean(userId?.isAdmin);
  const dispatch = useDispatch();

  const menuBySection = useMemo(() => {
    const filterByName = (names: string[]) => Menu.filter((item) => names.includes(item.name));
    return {
      principal: filterByName(principalItems),
      community: filterByName(communityItems),
      space: filterByName(spaceItems),
    };
  }, []);

  const mobileBottomItems = useMemo(() => {
    const nonChatCommunityItems = menuBySection.community.filter((item) => item.name !== 'Chat');
    return menuBySection.principal.slice(0, 3).concat(nonChatCommunityItems, menuBySection.space.slice(0, 1));
  }, [menuBySection]);

  useEffect(() => {
    window.addEventListener('scroll', stickNavbar); 
    return () => {
      window.removeEventListener('scroll', stickNavbar);
    };
  }, []);

  function isActive(pathname: string) {
    const currentPath = window.location.pathname;
    const current = currentPath.split('/');
    const path = pathname.split('/');
    return current[1] === path[1];
  }

  const stickNavbar = () => {
    if (window !== undefined) {
      const windowHeight = window.scrollY;
      windowHeight > 80 ? setStickyClass('shadow-[4px_0_32px_rgba(7,7,7,0.25)]') : setStickyClass('');
    }
  };

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(logOut());
  };

  const renderDesktopNavItem = (item: (typeof Menu)[number]) => {
    const badgeMeta = navBadges[item.name];
    const active = isActive(item.path);

    return (
      <Link
        to={item.path}
        key={item.path}
        className={`group flex items-center rounded-xl py-2.5 transition-all ${
          isCollapsed ? 'lg:justify-center lg:px-0' : 'gap-3 px-3'
        } ${
          active
            ? 'bg-gradient-to-r from-so-600 to-so-300 text-white shadow-[0_8px_24px_rgba(241,135,0,0.35)]'
            : 'text-sc-c2 hover:bg-white/10 hover:text-white'
        }`}
      >
        <img
          className={`h-5 w-auto max-w-[20px] flex-shrink-0 object-contain transition ${
            active ? 'brightness-0 invert' : 'nav-icon-default group-hover:brightness-0 group-hover:invert'
          }`}
          src={item.url}
          alt={item.name}
        />
        <span
          className={`text-[13px] tracking-wide ${
            isCollapsed ? 'lg:hidden' : 'md:hidden lg:inline'
          }`}
        >
          {item.name}
        </span>

        {badgeMeta?.count && (
          <span
            className={`ml-auto rounded-full bg-so-600 px-1.5 py-0.5 text-[10px] font-semibold text-white ${
              isCollapsed ? 'lg:hidden' : 'md:hidden lg:inline-flex'
            }`}
          >
            {badgeMeta.count}
          </span>
        )}

        {badgeMeta?.live && !isCollapsed && (
          <span className="ml-auto nav-live-badge hidden lg:inline-flex">
            <span className="nav-live-dot" />LIVE
          </span>
        )}
      </Link>
    );
  };

  const renderMobileNavItem = (item: (typeof Menu)[number]) => {
    const badgeMeta = navBadges[item.name];
    const active = isActive(item.path);

    return (
      <Link
        to={item.path}
        key={`mobile-${item.path}`}
        onClick={() => setShowVerticalMenu(false)}
        className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all ${
          active
            ? 'bg-gradient-to-r from-so-600 to-so-300 text-white shadow-[0_8px_24px_rgba(241,135,0,0.35)]'
            : 'text-sc-c2 hover:bg-white/10 hover:text-white'
        }`}
      >
        <img
          className={`h-5 w-5 flex-shrink-0 transition ${
            active ? 'brightness-0 invert' : 'nav-icon-default group-hover:brightness-0 group-hover:invert'
          }`}
          src={item.url}
          alt={item.name}
        />
        <span className="text-sm tracking-wide">{item.name}</span>

        {badgeMeta?.count && (
          <span className="ml-auto rounded-full bg-so-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
            {badgeMeta.count}
          </span>
        )}

        {badgeMeta?.live && (
          <span className="ml-auto nav-live-badge">
            <span className="nav-live-dot" />LIVE
          </span>
        )}
      </Link>
    );
  };

  return (
    <>
      <nav
        className={`platform-navbar relative z-30 hidden h-screen min-h-screen md:flex md:flex-col md:justify-between md:overflow-visible md:bg-gradient-to-b md:from-sb-800 md:via-sb-700 md:to-sb-800 md:py-6 md:sticky md:top-0 md:w-[72px] md:min-w-[72px] lg:py-7 ${
          isCollapsed ? 'lg:w-[72px] lg:min-w-[72px]' : 'lg:w-[240px] lg:min-w-[240px]'
        } ${stickyClass}`}
      >
        <button
          onClick={() => setIsCollapsed((prev) => !prev)}
          title={isCollapsed ? 'Expandir menu' : 'Colapsar menu'}
          aria-label={isCollapsed ? 'Expandir menu' : 'Colapsar menu'}
          className={`absolute top-1/2 z-40 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-sb-200 bg-[white] text-sb-700 shadow-lg transition hover:bg-so-600 hover:text-[white] lg:flex ${
            isCollapsed ? '-right-4 rotate-180' : '-right-4'
          }`}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M8.75 2.5L4.25 7L8.75 11.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="px-3 lg:px-4">
          <div className="mb-6 border-b border-white/10 pb-5">
            <Link to="/user" className="flex min-w-0 items-center gap-3">
              <StyledBadge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="dot"
              >
                <SmallAvatar {...stringAvatar(userName)} />
              </StyledBadge>

              <div className={`${isCollapsed ? 'lg:hidden' : 'hidden lg:block'} min-w-0`}>
                <p className="truncate text-sm font-medium text-white">{userName}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-so-300">Miembro Soberana</p>
              </div>
            </Link>

            <div className={`${isCollapsed ? 'lg:hidden' : 'hidden lg:block'} mt-4`}>
              <p className="font-['Playfair_Display'] text-base text-white">Sarasanas</p>
              <p className="text-[10px] uppercase tracking-[0.28em] text-so-300/80">Sanctuary · Practice</p>
            </div>
          </div>

          <div className="hide-scrollbar max-h-[calc(100vh-210px)] overflow-y-auto pr-1">
            <p className={`${isCollapsed ? 'lg:hidden' : 'hidden lg:block'} px-2 pb-2 pt-1 text-[10px] uppercase tracking-[0.25em] text-white/40`}>
              Principal
            </p>
            <div className="space-y-1">{menuBySection.principal.map(renderDesktopNavItem)}</div>

            <div className="my-3 h-px bg-white/10" />
            <p className={`${isCollapsed ? 'lg:hidden' : 'hidden lg:block'} px-2 pb-2 text-[10px] uppercase tracking-[0.25em] text-white/40`}>
              Comunidad
            </p>
            <div className="space-y-1">{menuBySection.community.map(renderDesktopNavItem)}</div>

            <div className="my-3 h-px bg-white/10" />
            <p className={`${isCollapsed ? 'lg:hidden' : 'hidden lg:block'} px-2 pb-2 text-[10px] uppercase tracking-[0.25em] text-white/40`}>
              Mi Espacio
            </p>
            <div className="space-y-1">{menuBySection.space.map(renderDesktopNavItem)}</div>
          </div>
        </div>

        <div className="border-t border-white/10 px-3 py-3 lg:px-4 lg:py-4">
          {isAdmin && (
            <Link
              to="/admin"
              className={`group mb-2 flex w-full items-center rounded-xl py-2.5 text-so-300 transition hover:bg-white/10 hover:text-so-100 ${isCollapsed ? 'lg:justify-center lg:px-0' : 'gap-3 px-3'}`}
            >
              <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.293 2.293a1 1 0 0 1 1.414 0l7 7A1 1 0 0 1 17 11h-1v6a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-3H8v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-6H3a1 1 0 0 1-.707-1.707l7-7Z" clipRule="evenodd" />
              </svg>
              <span className={`${isCollapsed ? 'lg:hidden' : 'hidden lg:inline'} text-[13px] font-semibold tracking-wide`}>Admin Panel</span>
            </Link>
          )}
          <Link
            to="/user"
            className={`group mb-2 flex w-full items-center rounded-xl py-2.5 text-sc-c2 transition hover:bg-white/10 hover:text-white ${
              isCollapsed ? 'lg:justify-center lg:px-0' : 'gap-3 px-3'
            }`}
          >
            <SettingsOutlinedIcon className="text-[20px] text-inherit" />
            <span className={`${isCollapsed ? 'lg:hidden' : 'hidden lg:inline'} text-[13px] tracking-wide`}>Configuración</span>
          </Link>

          <button
            onClick={handleLogout}
            className={`group flex w-full items-center rounded-xl py-2.5 text-sc-c2 transition hover:bg-red-500/15 hover:text-red-200 ${
              isCollapsed ? 'lg:justify-center lg:px-0' : 'gap-3 px-3'
            }`}
          >
            <img
              className="h-5 w-auto max-w-[20px] object-contain nav-icon-default group-hover:brightness-0 group-hover:invert"
              src="../../assets/icons/NavBar/exit.png"
              alt="Cerrar sesion"
            />
            <span className={`${isCollapsed ? 'lg:hidden' : 'hidden lg:inline'} text-[13px] tracking-wide`}>Cerrar Sesion</span>
          </button>
        </div>
      </nav>

      <header className="fixed left-0 right-0 top-0 z-40 flex h-16 items-center justify-between border-b-2 border-so-100 bg-white/95 px-4 backdrop-blur md:hidden">
        <button
          className="flex h-10 w-10 items-center justify-center rounded-lg text-sb-800"
          onClick={() => setShowVerticalMenu((prev) => !prev)}
          aria-label="Abrir menu"
        >
          {showVerticalMenu ? <CloseIcon /> : <MenuIcon />}
        </button>

        <div className="text-center leading-none">
          <p className="font-['Playfair_Display'] text-base text-sb-900">Mujer Soberana</p>
          <p className="text-[9px] uppercase tracking-[0.24em] text-sb-600">Sanctuary · Practice</p>
        </div>

        <Link to="/user">
          <SmallAvatar {...stringAvatar(userName)} sx={{ width: 36, height: 36, fontSize: 13 }} />
        </Link>
      </header>

      <div
        className={`fixed inset-0 z-40 bg-black/45 backdrop-blur-sm transition md:hidden ${
          showVerticalMenu ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={() => setShowVerticalMenu(false)}
      />

      <aside
        className={`fixed bottom-0 left-0 top-0 z-50 w-[240px] bg-gradient-to-b from-sb-800 via-sb-700 to-sb-800 p-4 shadow-2xl transition-transform md:hidden ${
          showVerticalMenu ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="mb-4 border-b border-white/10 pb-4">
          <div className="mb-3 flex items-center justify-between">
            <button onClick={() => setShowVerticalMenu(false)} aria-label="Cerrar menu" className="rounded-md p-1 text-sc-c2">
              <CloseIcon fontSize="small" />
            </button>
          </div>
          <Link to="/user" className="flex items-center gap-3" onClick={() => setShowVerticalMenu(false)}>
            <StyledBadge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              variant="dot"
            >
              <SmallAvatar {...stringAvatar(userName)} />
            </StyledBadge>
            <div>
              <p className="text-sm font-medium text-white">{userName}</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-so-300">Miembro Soberana</p>
            </div>
          </Link>
        </div>

        <div className="hide-scrollbar max-h-[calc(100vh-240px)] overflow-y-auto">
          <p className="px-2 pb-2 text-[10px] uppercase tracking-[0.25em] text-white/40">Principal</p>
          <div className="space-y-1">{menuBySection.principal.map(renderMobileNavItem)}</div>

          <div className="my-3 h-px bg-white/10" />
          <p className="px-2 pb-2 text-[10px] uppercase tracking-[0.25em] text-white/40">Comunidad</p>
          <div className="space-y-1">{menuBySection.community.map(renderMobileNavItem)}</div>

          <div className="my-3 h-px bg-white/10" />
          <p className="px-2 pb-2 text-[10px] uppercase tracking-[0.25em] text-white/40">Mi Espacio</p>
          <div className="space-y-1">{menuBySection.space.map(renderMobileNavItem)}</div>
        </div>

        {isAdmin && (
          <Link
            to="/admin"
            onClick={() => setShowVerticalMenu(false)}
            className="mt-4 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-so-300 transition hover:bg-white/10 hover:text-so-100"
          >
            <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.293 2.293a1 1 0 0 1 1.414 0l7 7A1 1 0 0 1 17 11h-1v6a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-3H8v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-6H3a1 1 0 0 1-.707-1.707l7-7Z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-semibold">Admin Panel</span>
          </Link>
        )}
        <Link
          to="/user"
          onClick={() => setShowVerticalMenu(false)}
          className="mt-4 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sc-c2 transition hover:bg-white/10 hover:text-white"
        >
          <SettingsOutlinedIcon className="text-[20px] text-inherit" />
          <span className="text-sm">Profile Settings</span>
        </Link>

        <button
          onClick={(e) => {
            handleLogout(e);
            setShowVerticalMenu(false);
          }}
          className="mt-2 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sc-c2 transition hover:bg-red-500/15 hover:text-red-200"
        >
          <img className="h-5 w-5 nav-icon-default" src="../../assets/icons/NavBar/exit.png" alt="Cerrar sesion" />
          <span className="text-sm">Cerrar Sesion</span>
        </button>
      </aside>

      <nav className="fixed bottom-0 left-0 right-0 z-40 flex h-[68px] items-center border-t border-sb-200 bg-gradient-to-r from-sc-c2/95 via-white/95 to-sc-c2/95 px-1 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_24px_rgba(36,64,66,0.18)] backdrop-blur md:hidden">
        {mobileBottomItems.map((item) => {
          const badgeMeta = navBadges[item.name];
          const active = isActive(item.path);

          return (
            <Link
              key={`bottom-${item.path}`}
              to={item.path}
              className={`relative mx-0.5 flex flex-1 flex-col items-center justify-center gap-1 rounded-lg py-1 text-[9px] uppercase tracking-wide transition ${
                active ? 'bg-white/80 text-so-700' : 'text-sb-700'
              }`}
            >
              <img
                className={`h-4 w-4 ${
                  active
                    ? 'brightness-0 saturate-100 invert-[59%] sepia-[72%] saturate-[1363%] hue-rotate-[2deg] brightness-[101%] contrast-[102%]'
                    : 'nav-icon-mobile-inactive'
                }`}
                src={item.url}
                alt={item.name}
              />
              {item.name === 'Clases de Yoga' ? 'Yoga' : item.name}

              {active && <span className="absolute top-1 h-1 w-1 rounded-full bg-so-600" />}

              {badgeMeta?.count && (
                <span className="absolute right-[calc(50%-16px)] top-1 min-w-4 rounded-full border border-white bg-so-600 px-1 text-[8px] font-semibold text-white">
                  {badgeMeta.count}
                </span>
              )}

              {badgeMeta?.live && <span className="absolute right-[calc(50%-12px)] top-1 h-2 w-2 rounded-full border border-white bg-red-500 nav-live-dot" />}
            </Link>
          );
        })}
      </nav>
    </>
  );
};

export default VerticalNavBar;
